// «¿Cuánto te debo?» — la respuesta cuando suena el teléfono.
//
// La pregunta parece una sola y son dos, según quién llame:
//
//   el DUEÑO DE FINCA    le vende leche. Usted le debe a él. Pero si le prestó
//                        plata, esa se descuenta: la respuesta no es lo que
//                        entregó, es lo que va a salir de la caja.
//   el COMPRADOR         le compró queso fiado. Él le debe a usted.
//
// Y una misma persona puede ser las dos cosas — le vende la leche y le compra
// el queso—, así que ninguna de las dos respuestas sirve sola.
//
// Aquí solo se hace la cuenta. Quién es cada quien lo dice el servidor.

const num = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

// Lo que hay entre usted y una finca.
//
//   leche     lo que le entregó y no se le ha pagado
//   prestado  lo que ella le debe de plata prestada, sin descontar
//   neto      LO QUE VA A SALIR DE LA CAJA
//
// El neto es el número de la conversación. Contestar «le debo $1.000.000»
// teniendo $400.000 prestados sin descontar es regalar esa plata: quedó dicho
// por teléfono y después la discusión ya está perdida.
export function cuentaDeFinca(finca) {
  if (!finca) return null;
  // Un saldo negativo no es una deuda al revés: es que se le pagó de más, y
  // eso no se descuenta contra un préstamo sin decírselo a nadie.
  const leche = Math.max(0, num(finca.saldo));
  const prestado = Math.max(0, num(finca.prestado_pendiente));
  const neto = leche - prestado;

  return {
    leche,
    prestado,
    // Lo que sale de la caja hoy. Nunca menos de cero: un pago negativo no
    // existe. Si el préstamo es más grande que la leche, se descuenta hasta
    // donde alcanza y el resto queda debiendo —que es justo lo que hace el
    // servidor al liquidar—.
    pagar: Math.max(0, neto),
    // Lo que le sigue debiendo del préstamo después de este pago.
    quedaDebiendo: Math.max(0, -neto),
    // Sin préstamos de por medio la respuesta es la plana, y decirlo evita
    // pintar un descuento donde no hay ninguno.
    hayDescuento: prestado > 0,
  };
}

// Lo que un comprador le debe a usted.
//
//   despachos  las ventas fiadas que le quedan pendientes, ya con los abonos
//              repartidos (viene de `creditosPendientes`)
export function cuentaDeCliente(cliente, despachos) {
  if (!cliente) return null;
  const suyos = (despachos || []).filter(d => d.cliente_id === cliente.id);
  const debe = Math.max(0, num(cliente.saldo));

  return {
    debe,
    // La plata que abonó de más, que es a favor de él. Se dice aparte y no
    // como una deuda negativa: «−$200.000 debe» se lee como un error del
    // programa, no como un saldo a favor.
    aFavor: Math.max(0, -num(cliente.saldo)),
    despachos: suyos,
    // El que más lleva sin pagar. Es lo que se le nombra por teléfono: un
    // total no le recuerda nada a nadie, una fecha sí.
    masViejo: suyos.length ? suyos[0] : null,
  };
}

// Las dos cuentas juntas, para la persona que es finca Y cliente.
//
// Se devuelve el neto de todo porque es lo que van a hablar: si usted le debe
// $800.000 de leche y él le debe $300.000 de queso, lo que se van a pasar son
// $500.000. Ninguna de las dos cifras sueltas contesta la llamada.
export function neto({ fincas, clientes }) {
  let leDebeUsted = 0, leDebenAUsted = 0;
  for (const f of fincas || []) {
    const c = cuentaDeFinca(f);
    leDebeUsted += c.pagar;
    leDebenAUsted += c.quedaDebiendo;   // lo que quedó del préstamo sin cubrir
  }
  for (const c of clientes || []) leDebenAUsted += Math.max(0, num(c.saldo));

  const saldo = leDebenAUsted - leDebeUsted;
  return {
    leDebeUsted,
    leDebenAUsted,
    saldo,
    // Quién le queda debiendo a quién, dicho con palabras. El signo de un
    // número no le dice a nadie de qué lado está la plata.
    aFavorDeQuien: saldo > 0 ? 'usted' : saldo < 0 ? 'el otro' : 'nadie',
  };
}
